import { z } from 'zod'

/**
 * School feature health: the stored verdict per school per feature (plan
 * docs/_archive/2026-10-17-plans-sweep/plans/2026-09-12-2056-feat-school-feature-health-plan.md, KTD2, KTD16).
 *
 * THE REGISTRY IS NOT HERE. What a "feature" is lives in the backend registry
 * (`apps/backend/src/services/feature-health/registry.ts`, KTD1); the endpoints
 * join registry rows to stored verdicts and hand readers display names, order
 * and next-action text. A feature key is therefore a plain string in this file,
 * so adding a registry row is never a contract change.
 *
 * NO PERSONAL DATA (KTD16). The evidence object is strict: counts, timestamps,
 * sync-type, feature and module keys, Blackbaud role names from the coverage
 * map, and enum codes. No names, emails, user ids, section ids or internal
 * bigint ids — offending records are counted, never listed. Reason and
 * evidence text reaches the internal endpoint, the CLI and agent prompts.
 */

/**
 * The nine verdict states (R11). `ok`, `degraded`, `failing` and `unknown`
 * keep the meaning of the `sj` verdict vocabulary; the other five are
 * feature-health's own and do not contribute to the school roll-up except
 * `blocked` (R15).
 */
export const FEATURE_HEALTH_STATES = [
  'ok',
  'degraded',
  'failing',
  'blocked',
  'never-active',
  'disabled',
  'n/a',
  'not-graded',
  'unknown'
] as const
export const FeatureHealthStateSchema = z.enum(FEATURE_HEALTH_STATES)
export type FeatureHealthState = z.infer<typeof FeatureHealthStateSchema>

/** The states a school roll-up can take: worst-of over the contributing states (R15). */
export const FEATURE_HEALTH_ROLLUP_STATES = ['ok', 'degraded', 'failing', 'blocked', 'unknown'] as const
export const FeatureHealthRollupStateSchema = z.enum(FEATURE_HEALTH_ROLLUP_STATES)
export type FeatureHealthRollupState = z.infer<typeof FeatureHealthRollupStateSchema>

/** What caused a computation (R12). Only `sweep` writes may take a clearing edge (KTD5). */
export const FEATURE_HEALTH_TRIGGERS = ['sweep', 'event', 'refresh'] as const
export const FeatureHealthTriggerSchema = z.enum(FEATURE_HEALTH_TRIGGERS)
export type FeatureHealthTrigger = z.infer<typeof FeatureHealthTriggerSchema>

/**
 * Outcome of the last attempt to read one evidence source. Spelled the same as
 * the push-channel check status on purpose: `stale_check` is the column default
 * and is never a reading; `unreadable` keeps the previous facts in place.
 */
export const FEATURE_HEALTH_CHECK_STATUSES = ['observed', 'unreadable', 'stale_check'] as const
export const FeatureHealthCheckStatusSchema = z.enum(FEATURE_HEALTH_CHECK_STATUSES)
export type FeatureHealthCheckStatus = z.infer<typeof FeatureHealthCheckStatusSchema>

/** The evidence sources a verdict reads, each with its own check status column. */
export const FEATURE_HEALTH_SOURCES = ['config', 'integration', 'coverage', 'output', 'attempts'] as const
export const FeatureHealthSourceSchema = z.enum(FEATURE_HEALTH_SOURCES)
export type FeatureHealthSource = z.infer<typeof FeatureHealthSourceSchema>

/**
 * Fixed error codes for an evaluator failure (R20). The reason carries the code
 * and the source name; raw error text goes only to the scrubbed check-error
 * column, never into the reason or the evidence.
 */
export const FEATURE_HEALTH_ERROR_CODES = [
  'QUERY_FAILED',
  'SOURCE_STALE',
  'SOURCE_UNREADABLE',
  'FACET_LIMIT_FILLED',
  'EMPTY_AGGREGATE',
  'NO_CURRENT_YEAR'
] as const
export const FeatureHealthErrorCodeSchema = z.enum(FEATURE_HEALTH_ERROR_CODES)
export type FeatureHealthErrorCode = z.infer<typeof FeatureHealthErrorCodeSchema>

/** The persona a subcomponent serves (R3). `school` is a part with no persona split. */
export const FEATURE_HEALTH_PERSONAS = ['teacher', 'parent', 'student', 'school'] as const
export const FeatureHealthPersonaSchema = z.enum(FEATURE_HEALTH_PERSONAS)
export type FeatureHealthPersona = z.infer<typeof FeatureHealthPersonaSchema>

/** Unit of a row's secondary number (R10). */
export const FEATURE_HEALTH_SECONDARY_UNITS = ['hours', 'percent', 'count'] as const
export const FeatureHealthSecondaryUnitSchema = z.enum(FEATURE_HEALTH_SECONDARY_UNITS)
export type FeatureHealthSecondaryUnit = z.infer<typeof FeatureHealthSecondaryUnitSchema>

/** Why a row reads blocked (R5, R33). */
export const FEATURE_HEALTH_BLOCKED_CAUSES = ['integration-down', 'role-gap'] as const
export const FeatureHealthBlockedCauseSchema = z.enum(FEATURE_HEALTH_BLOCKED_CAUSES)
export type FeatureHealthBlockedCause = z.infer<typeof FeatureHealthBlockedCauseSchema>

/**
 * Named adapter counts carried in `evidence.metrics`. An enum rather than free
 * keys so the evidence object stays strict (KTD16); adding an adapter metric is
 * an additive change here.
 */
export const FEATURE_HEALTH_METRIC_KEYS = [
  // News Feed
  'contentPublished',
  'feedActivitiesDelivered',
  'publishToFeedLagHoursAvg',
  // Assignment Grades
  'gradeRows',
  'enrolledStudents',
  'studentsWithGrade',
  'syncJobsRun',
  'syncJobsZeroItems',
  // Class Schedule
  'sectionsTotal',
  'sectionsWithMembers',
  // Directory
  'usersSynced',
  // Push
  'pushDeliveriesSuccessful',
  'pushDeliveriesFailed',
  'pushNotificationsInspected',
  'pushChannelsObserved',
  'pushCheckAgeHours',
  // Attendance
  'attendanceWritesSucceeded',
  'attendanceWritesFailed',
  'parentAbsencesSucceeded',
  'parentAbsencesFailed'
] as const
export const FeatureHealthMetricKeySchema = z.enum(FEATURE_HEALTH_METRIC_KEYS)
export type FeatureHealthMetricKey = z.infer<typeof FeatureHealthMetricKeySchema>

const isoDateTime = z.string().datetime()
/** A UTC calendar day, `YYYY-MM-DD`. */
const utcDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
/** A sync-type constant (`STUDENT_GRADED_ASSIGNMENTS_BULK`), never free text. */
const syncTypeKey = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/)
/** A registry, subcomponent or coverage-module key (`assignment-grades`). */
const kebabKey = z.string().regex(/^[a-z][a-z0-9-]{0,47}$/)
/** A Blackbaud role name from the coverage map (`Platform Manager`). Letters, spaces and `/` only. */
const roleName = z.string().regex(/^[A-Za-z][A-Za-z /-]{0,63}$/)
const count = z.number().int().nonnegative()

/**
 * The fourteen-day window and baseline numbers behind a graded row (R13). Null
 * means never read; zero means read and empty (KTD2).
 */
export const FeatureHealthWindowSchema = z.strictObject({
  /** Output over the fourteen days ending today, today's partial output included. */
  output14d: count.nullable(),
  /** Attempts over the same window; null when the row declares no attempt signal. */
  attempts14d: count.nullable(),
  /** Attempts that produced nothing (zero-item jobs, failed writes). */
  attemptsWithoutOutput14d: count.nullable(),
  /** Output over the trailing seven complete UTC days (today excluded). */
  output7d: count.nullable(),
  /** Median of the prior four seven-day sums; null while the baseline is building. */
  baselineMedian7d: z.number().nonnegative().nullable(),
  /** Observed applicable complete days behind the baseline, 0..28 (R37). */
  baselineDays: z.number().int().min(0).max(28),
  /** Newest UTC day with output above zero, today included. */
  newestOutputDay: utcDay.nullable(),
  /** Age of that day in school days (weekdays inside a term range). */
  newestOutputAgeSchoolDays: count.nullable(),
  /** Today's partial output. */
  todayOutput: count.nullable()
})
export type FeatureHealthWindow = z.infer<typeof FeatureHealthWindowSchema>

/** A subcomponent's state as captured at evaluation time (R3, R22). */
export const FeatureHealthSubcomponentEvidenceSchema = z.strictObject({
  key: kebabKey,
  persona: FeatureHealthPersonaSchema,
  state: FeatureHealthStateSchema,
  reason: z.string().max(240),
  window: FeatureHealthWindowSchema.nullable()
})
export type FeatureHealthSubcomponentEvidence = z.infer<typeof FeatureHealthSubcomponentEvidenceSchema>

/** A feeding sync type's last terminal job, captured at evaluation time (R22). */
export const FeatureHealthSyncEvidenceSchema = z.strictObject({
  syncType: syncTypeKey,
  /** `SUCCESS`, `PARTIAL_SUCCESS`, `FAILED`... or null when no terminal job is retained (15 days). */
  lastTerminalStatus: syncTypeKey.nullable(),
  lastTerminalAt: isoDateTime.nullable(),
  lastItemsProcessed: count.nullable()
})
export type FeatureHealthSyncEvidence = z.infer<typeof FeatureHealthSyncEvidenceSchema>

/** One enabling switch and its value at evaluation time (R4, R22). */
export const FeatureHealthSwitchEvidenceSchema = z.strictObject({
  scope: z.enum(['global', 'screen']),
  /** Dotted path into the feature config, e.g. `personDetail.features.schedule.enabled`. */
  path: z.string().regex(/^[A-Za-z][A-Za-z0-9.-]{0,127}$/),
  /** Null when the config could not be read. */
  on: z.boolean().nullable()
})
export type FeatureHealthSwitchEvidence = z.infer<typeof FeatureHealthSwitchEvidenceSchema>

/** One required coverage module's state from the stored snapshot (R5, R22). */
export const FeatureHealthCoverageModuleEvidenceSchema = z.strictObject({
  key: kebabKey,
  required: z.boolean(),
  criticality: z.enum(['REQUIRED', 'OPTIONAL']),
  /** Null until a snapshot exists (KTD8). */
  status: z.enum(['available', 'degraded', 'dead']).nullable(),
  missingRoles: z.array(roleName)
})
export type FeatureHealthCoverageModuleEvidence = z.infer<typeof FeatureHealthCoverageModuleEvidenceSchema>

/**
 * The strict evidence object stored on every verdict row (KTD16). Everything the
 * expanded row renders is captured here at evaluation time, so no request reads
 * an evidence source.
 */
export const FeatureHealthEvidenceSchema = z.strictObject({
  window: FeatureHealthWindowSchema.nullable(),
  metrics: z.partialRecord(FeatureHealthMetricKeySchema, z.number().nonnegative().nullable()),
  blocked: z
    .strictObject({
      cause: FeatureHealthBlockedCauseSchema,
      since: isoDateTime.nullable(),
      missingRoles: z.array(roleName),
      modules: z.array(kebabKey)
    })
    .nullable(),
  error: z.strictObject({ code: FeatureHealthErrorCodeSchema, source: FeatureHealthSourceSchema }).nullable(),
  /** The clear streak after this computation, when the row is clearing (R13). */
  clearing: z.strictObject({ streak: count, needed: z.number().int().positive() }).nullable(),
  /** A break day or a school with no term dates (R34, KTD9). */
  calendar: z.strictObject({ breakDay: z.boolean(), termDates: z.boolean() }),
  sources: z.record(FeatureHealthSourceSchema, FeatureHealthCheckStatusSchema),
  subcomponents: z.array(FeatureHealthSubcomponentEvidenceSchema),
  switches: z.array(FeatureHealthSwitchEvidenceSchema),
  syncs: z.array(FeatureHealthSyncEvidenceSchema),
  coverage: z.strictObject({
    status: z.enum(['probed', 'not-probed', 'unavailable']),
    probedAt: isoDateTime.nullable(),
    modules: z.array(FeatureHealthCoverageModuleEvidenceSchema)
  })
})
export type FeatureHealthEvidence = z.infer<typeof FeatureHealthEvidenceSchema>

/**
 * A verdict as written to `SchoolFeatureHealthVerdict` (KTD2): state, trigger and
 * check-status columns are strings on the table and validated by this schema on
 * write, so a tenth state needs no promotion.
 */
export const FeatureHealthVerdictSchema = z.strictObject({
  featureKey: kebabKey,
  state: FeatureHealthStateSchema,
  previousState: FeatureHealthStateSchema.nullable(),
  clearStreak: count,
  reason: z.string().min(1).max(240),
  nextAction: z.string().max(240).nullable(),
  evidence: FeatureHealthEvidenceSchema,
  secondaryValue: z.number().int().nullable(),
  secondaryUnit: FeatureHealthSecondaryUnitSchema.nullable(),
  trigger: FeatureHealthTriggerSchema,
  computedAt: isoDateTime
})
export type FeatureHealthVerdict = z.infer<typeof FeatureHealthVerdictSchema>
